// de-front/de-order-admin/src/components/purchase-sales/detail-sidebar.tsx
import { Box, Divider, Group, Stack, Text } from "@mantine/core"
import { FileTextIcon, TruckIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next"
import { IOrderPlus } from "../../models/Order"
import { currencyFormat } from "../../utils"
import { dateFormat, dateTimeSecFormat } from "../../utils/date"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import { DetailRow } from "../order/detail-card"
import { CodeBadge } from "../ui/code-badge"
import { SmartLink } from "../ui/smart-link"
import classes from "./detail-sidebar.module.css"
import { OrderStatusTag } from "./status-tag"

type Props = {
  data: IOrderPlus
  /** Effective status code shown as a dot + word under the PO number. */
  status?: string | null
}

/** Left card of Ord_PurchaseDetail: PO code + status, parties, key info, grey total box. Sentence-case labels, no icons. */
export function DetailSidebar({ data, status }: Props) {
  const { t } = useTranslation(["supplierSales", "order", "common"])

  const createdBy = renderName(data.orderedUser)

  const buyer = data.senderBusiness
  const supplier = data.receiverBusiness

  const party = (label: string, business: typeof buyer) => (
    <Box>
      <Text className={classes.label}>{label}</Text>
      <Text className={classes.partyName}>{renderBusinessName(business)}</Text>
      {business?.regNumber && (
        <Text className={classes.label}>
          {t("supplierSales:taxId")}: {business.regNumber}
        </Text>
      )}
    </Box>
  )

  return (
    <Box className={classes.root}>
      {/* Header: PO code + badges */}
      <Text ff="monospace" className={classes.code}>
        {data.purchaseCode || `PO-${data.id?.slice(0, 8)}`}
      </Text>
      <Group gap={6} mt="xs" wrap="wrap">
        <OrderStatusTag code={status} perspective="received" />
        {data.salesCode && <CodeBadge size="sm" variant="default">SO {data.salesCode}</CodeBadge>}
      </Group>

      <Divider my="md" color="gray.2" />

      {/* Parties */}
      <Text className={classes.section}>{t("supplierSales:purchase.detail.sidebar.parties")}</Text>
      <Stack gap="sm" mt="xs">
        {party(t("supplierSales:purchase.detail.sidebar.buyer"), buyer)}
        {party(t("supplierSales:purchase.detail.sidebar.supplier"), supplier)}
      </Stack>

      <Divider my="md" color="gray.2" />

      {/* Key info */}
      <Text className={classes.section}>{t("supplierSales:purchase.detail.sidebar.info")}</Text>
      <Stack gap="xs" mt="xs">
        <DetailRow label={t("supplierSales:purchase.detail.sidebar.delivery")} mono>
          {data.deliveryDate ? dateFormat(data.deliveryDate) : "-"}
        </DetailRow>
        <DetailRow label={t("supplierSales:purchase.detail.sidebar.createdAt")} mono>
          {data.createdAt ? dateTimeSecFormat(data.createdAt) : "-"}
        </DetailRow>
        <DetailRow label={t("supplierSales:purchase.detail.sidebar.createdBy")}>{createdBy}</DetailRow>
        <DetailRow label={t("supplierSales:purchase.detail.sidebar.paymentTerm")}>
          {data.paymentTerm?.description || "-"}
        </DetailRow>
      </Stack>

      {/* Total amount: grey box, big mono amount */}
      <Box className={classes.totalCard} mt="lg">
        <Text className={classes.label}>{t("supplierSales:purchase.detail.sidebar.totalAmount")}</Text>
        <Text ff="monospace" className={classes.total}>{currencyFormat(data.totalAmount || 0)}</Text>
        <Text className={classes.label}>
          {data.itemTypeCount || 0} {t("order:purchaseSales.type")} · {t("supplierSales:purchase.detail.sidebar.vatIncluded")}
        </Text>
      </Box>

      {/* Smart links */}
      {(data.invoiceId || data.deliveryNote) && (
        <>
          <Divider my="md" color="gray.2" />
          <Stack gap={6}>
            {data.invoiceId && (
              <SmartLink
                icon={<FileTextIcon size={14} color="var(--mantine-color-dark-6)" />}
                label={t("supplierSales:purchase.invoice")}
                trailing={1}
              />
            )}
            {data.deliveryNote && (
              <SmartLink
                icon={<TruckIcon size={14} color="var(--mantine-color-dark-6)" />}
                label={t("supplierSales:purchase.delivery")}
                trailing={1}
              />
            )}
          </Stack>
        </>
      )}
    </Box>
  )
}
